<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useEvents } from '@/composables/useEvents'
import { useSubscription } from '@/composables/useSubscription'
import { useToasts } from '@/composables/useToasts'
import { scopeLabels, severityLabels } from '@/i18n'

const { lang, t } = useI18n()
const { servicesList } = useEvents()
const { isConfirmed, isPending, storedEmail, getStoredServices, getStoredSeverities, validateEmail, subscribe, updatePreferences, preferencesToken, preferencesData, getRssUrl, getAtomUrl, getSlackCommand, copyToClipboard } = useSubscription()
const { showToast } = useToasts()

const isOpen = ref(false)
const activeTab = ref<'email' | 'feeds' | 'slack'>('email')
const email = ref('')
const privacyChecked = ref(false)
const submitting = ref(false)
const showSuccess = ref(false)
const emailError = ref('')
const copiedFeedBtn = ref<string | null>(null)

// Selectable scopes and severities
const selectedScopes = ref<Set<string>>(new Set())
const selectedSeverities = ref<Set<string>>(new Set())

// Available scopes
const availableScopes = computed(() => {
  const scopeSet = new Set(servicesList.value.map((s) => s.scope).filter(Boolean))
  return Array.from(scopeSet)
})

// Available severity keys
const availableSeverities = computed(() => Object.keys(severityLabels.en))

// Initialize selections from stored or defaults
function initSelections() {
  const storedServices = getStoredServices()

  // Determine which scopes are fully selected
  selectedScopes.value = new Set()
  availableScopes.value.forEach((scope) => {
    const scopeServices = servicesList.value.filter((s) => s.scope === scope)
    const allSelected = scopeServices.every((s) => storedServices.includes(s.name))
    if (allSelected) selectedScopes.value.add(scope)
  })

  const storedSev = getStoredSeverities()
  selectedSeverities.value = new Set(storedSev)
}

function initSelectionsFromPreferences(services: string[], severities: string[]) {
  // Determine which scopes are fully selected based on the subscription's services
  selectedScopes.value = new Set()
  availableScopes.value.forEach((scope) => {
    const scopeServices = servicesList.value.filter((s) => s.scope === scope)
    const allSelected = scopeServices.every((s) => services.includes(s.name))
    if (allSelected) selectedScopes.value.add(scope)
  })
  selectedSeverities.value = new Set(severities)
}

const showSeverities = ref(false)

// Are we in preferences-editing mode?
const isPreferencesMode = computed(() => isConfirmed.value || !!preferencesToken.value)

// Computed state for submit button
const canSubmit = computed(() => {
  if (showSuccess.value) return false
  if (isPreferencesMode.value) return true
  if (isConfirmed.value) return false
  return privacyChecked.value
})

const submitButtonLabel = computed(() => {
  if (isPreferencesMode.value) return t('preferences-save')
  if (isConfirmed.value) return t('subscribe-subscribed')
  return t('subscribe-submit')
})

const maskedEmail = computed(() => {
  if (preferencesData.value?.maskedEmail) return preferencesData.value.maskedEmail
  if (storedEmail.value && storedEmail.value.includes('@')) return storedEmail.value
  return ''
})

function open() {
  isOpen.value = true
  activeTab.value = 'email'
  email.value = isPreferencesMode.value ? '' : storedEmail.value
  privacyChecked.value = false
  showSuccess.value = false
  emailError.value = ''
  if (isPreferencesMode.value && preferencesData.value) {
    initSelectionsFromPreferences(preferencesData.value.services, preferencesData.value.severities)
  } else {
    initSelections()
  }
  document.body.style.overflow = 'hidden'
}

function close() {
  isOpen.value = false
  document.body.style.overflow = ''
}

function setTab(tab: 'email' | 'feeds' | 'slack') {
  activeTab.value = tab
}

function toggleScope(scope: string) {
  if (selectedScopes.value.has(scope)) {
    selectedScopes.value.delete(scope)
  } else {
    selectedScopes.value.add(scope)
  }
}

function toggleSeverity(severity: string) {
  if (selectedSeverities.value.has(severity)) {
    selectedSeverities.value.delete(severity)
  } else {
    selectedSeverities.value.add(severity)
  }
}

function onEmailBlur() {
  if (email.value) {
    const v = validateEmail(email.value)
    if (!v.valid) emailError.value = v.error || t('subscribe-email-invalid')
    else emailError.value = ''
  } else {
    emailError.value = ''
  }
}

async function handleSubmit() {
  if (isPreferencesMode.value) {
    // Preferences update mode - no email/privacy needed
    submitting.value = true
    const token = preferencesToken.value || ''
    const ok = await updatePreferences(token, Array.from(selectedScopes.value), Array.from(selectedSeverities.value))
    submitting.value = false

    if (ok) {
      showSuccess.value = true
      setTimeout(() => close(), 2000)
    }
    return
  }

  const v = validateEmail(email.value)
  if (!v.valid) {
    emailError.value = v.error || t('subscribe-email-invalid')
    return
  }
  emailError.value = ''

  if (!privacyChecked.value) return

  submitting.value = true
  const ok = await subscribe(email.value, Array.from(selectedScopes.value), Array.from(selectedSeverities.value))
  submitting.value = false

  if (ok) {
    showSuccess.value = true
    setTimeout(() => close(), 2000)
  }
}

async function onCopyFeed(type: string, text: string) {
  const ok = await copyToClipboard(text)
  if (ok) {
    copiedFeedBtn.value = type
    setTimeout(() => {
      copiedFeedBtn.value = null
    }, 1200)
  }
}

function openDataPrivacy(e: Event) {
  e.preventDefault()
  e.stopPropagation()
  document.dispatchEvent(new CustomEvent('open-data-privacy-modal'))
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

function handleOpenEvent() {
  open()
}

onMounted(() => {
  document.addEventListener('open-subscription-modal', handleOpenEvent)
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('open-subscription-modal', handleOpenEvent)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div v-if="isOpen" class="modal">
    <div class="modal-backdrop" @click="close"></div>
    <div class="modal-dialog">
      <div class="modal-header">
        <h3>{{ isPreferencesMode ? t('preferences-title') : t('subscribe-email-modal-title') }}</h3>
        <button type="button" class="icon-btn" aria-label="Close" @click="close">×</button>
      </div>
      <div class="modal-content">
        <!-- Tabs -->
        <div class="subscription-tabs" role="tablist" :aria-label="t('subscribe')">
          <button
            type="button"
            :class="['subscription-tab', { active: activeTab === 'email' }]"
            :aria-selected="activeTab === 'email'"
            role="tab"
            @click="setTab('email')"
          >
            <span class="tab-icon icon icon-envelope" aria-hidden="true"></span>
            <span class="tab-label">{{ t('subscribe-tab-email') }}</span>
          </button>
          <button
            type="button"
            :class="['subscription-tab', { active: activeTab === 'feeds' }]"
            :aria-selected="activeTab === 'feeds'"
            role="tab"
            @click="setTab('feeds')"
          >
            <span class="tab-icon icon icon-rss" aria-hidden="true"></span>
            <span class="tab-label">{{ t('subscribe-tab-feeds') }}</span>
          </button>
          <button
            type="button"
            :class="['subscription-tab', { active: activeTab === 'slack' }]"
            :aria-selected="activeTab === 'slack'"
            role="tab"
            @click="setTab('slack')"
          >
            <span class="tab-icon icon icon-slack" aria-hidden="true"></span>
            <span class="tab-label">{{ t('subscribe-tab-slack') }}</span>
          </button>
        </div>

        <!-- Email Tab -->
        <div v-show="activeTab === 'email'" :class="['subscription-tab-panel', { active: activeTab === 'email' }]" role="tabpanel">
          <form v-if="!showSuccess" class="subscription-form" @submit.prevent="handleSubmit">
            <!-- Preferences mode header -->
            <p v-if="isPreferencesMode" class="preferences-info">{{ t('preferences-info') }}</p>

            <label v-if="isPreferencesMode" class="form-field">
              <span>{{ t('subscribe-email-address') }}</span>
              <input
                type="email"
                :value="maskedEmail"
                readonly
                class="email-verified"
              >
            </label>

            <label v-if="!isPreferencesMode" class="form-field">
              <span>{{ t('subscribe-email-address') }}</span>
              <input
                v-model="email"
                type="email"
                placeholder="your@email.com"
                required
                :readonly="isConfirmed"
                :class="{ 'email-verified': isConfirmed, 'email-pending': isPending && !isConfirmed, 'email-error': !!emailError }"
                @blur="onEmailBlur"
                @focus="emailError = ''"
              >
              <span v-if="emailError" class="field-error">{{ emailError }}</span>
            </label>

            <!-- Scopes (Services grouped by scope) -->
            <div class="form-field">
              <label>{{ t('subscribe-services') }}</label>
              <p class="field-help">{{ t('subscribe-select-services') }}</p>
              <div class="pills-container">
                <button
                  v-for="scope in availableScopes"
                  :key="scope"
                  type="button"
                  :class="['pill', 'pill-service', { 'pill-selected': selectedScopes.has(scope) }]"
                  @click="toggleScope(scope)"
                >
                  {{ scopeLabels[lang]?.[scope] || scope }}
                </button>
              </div>
            </div>

            <!-- Severities (collapsible) -->
            <div class="form-field">
              <button
                type="button"
                class="collapsible-toggle"
                :aria-expanded="showSeverities"
                @click="showSeverities = !showSeverities"
              >
                <span class="toggle-icon"></span>
                <span>{{ t('subscribe-severities') }}</span>
              </button>
              <div v-if="showSeverities" class="collapsible-content">
                <p class="field-help">{{ t('subscribe-select-severities') }}</p>
                <div class="pills-container">
                  <button
                    v-for="sev in availableSeverities"
                    :key="sev"
                    type="button"
                    :class="['pill', 'pill-severity', `severity-${sev}`, { 'pill-selected': selectedSeverities.has(sev) }]"
                    @click="toggleSeverity(sev)"
                  >
                    {{ severityLabels[lang]?.[sev] || sev }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Privacy checkbox -->
            <div v-if="!isConfirmed && !isPreferencesMode" class="form-field checkbox-field">
              <label class="checkbox-label">
                <input v-model="privacyChecked" type="checkbox">
                <span class="checkbox-text">
                  {{ t('subscribe-privacy-prefix') }}
                  <a href="#" @click="openDataPrivacy">{{ t('data-privacy') }}</a>
                  {{ t('subscribe-privacy-suffix') }}
                </span>
              </label>
            </div>
          </form>

          <div v-if="showSuccess" class="subscription-success-message">
            <p>{{ isPreferencesMode ? t('preferences-updated') : t('subscribe-success') }}</p>
          </div>
        </div>

        <!-- Feeds Tab -->
        <div v-show="activeTab === 'feeds'" :class="['subscription-tab-panel', { active: activeTab === 'feeds' }]" role="tabpanel">
          <div class="subscription-feed-links">
            <p>{{ t('subscribe-feed-links') }}</p>
            <div class="feed-links">
              <div class="feed-field">
                <label class="feed-label">{{ t('subscribe-get-rss') }}</label>
                <div class="feed-input-row">
                  <input type="text" class="feed-input" readonly :value="getRssUrl()" @focus="($event.target as HTMLInputElement).select()">
                  <button
                    type="button"
                    :class="['feed-copy-btn', { copied: copiedFeedBtn === 'rss' }]"
                    @click="onCopyFeed('rss', getRssUrl())"
                  >
                    {{ copiedFeedBtn === 'rss' ? t('subscribe-copied-short') : t('subscribe-copy') }}
                  </button>
                </div>
              </div>
              <div class="feed-field">
                <label class="feed-label">{{ t('subscribe-get-atom') }}</label>
                <div class="feed-input-row">
                  <input type="text" class="feed-input" readonly :value="getAtomUrl()" @focus="($event.target as HTMLInputElement).select()">
                  <button
                    type="button"
                    :class="['feed-copy-btn', { copied: copiedFeedBtn === 'atom' }]"
                    @click="onCopyFeed('atom', getAtomUrl())"
                  >
                    {{ copiedFeedBtn === 'atom' ? t('subscribe-copied-short') : t('subscribe-copy') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Slack Tab -->
        <div v-show="activeTab === 'slack'" :class="['subscription-tab-panel', { active: activeTab === 'slack' }]" role="tabpanel">
          <div class="subscription-feed-links subscription-slack">
            <p>{{ t('subscribe-slack-instructions') }}</p>
            <div class="feed-field">
              <label class="feed-label">{{ t('subscribe-slack-command-label') }}</label>
              <div class="feed-input-row">
                <input type="text" class="feed-input" readonly :value="getSlackCommand()" @focus="($event.target as HTMLInputElement).select()">
                <button
                  type="button"
                  :class="['feed-copy-btn', { copied: copiedFeedBtn === 'slack' }]"
                  @click="onCopyFeed('slack', getSlackCommand())"
                >
                  {{ copiedFeedBtn === 'slack' ? t('subscribe-copied-short') : t('subscribe-copy') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" @click="close">{{ t('cancel') }}</button>
        <button
          v-if="activeTab === 'email'"
          type="button"
          :class="['btn', 'btn-primary', { loading: submitting }]"
          :disabled="!canSubmit || submitting || showSuccess"
          @click="handleSubmit"
        >
          {{ submitButtonLabel }}
        </button>
      </div>
    </div>
  </div>
</template>
